import { CheckSquare, Funnel, Square } from '@phosphor-icons/react';
import {
  BELL_FILTER_BUTTON_TOOLTIP,
  getBellFilterGroupsForRole,
  isBellFilterGroupHidden,
  type NotificationType,
} from '@ruta/shared';
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface NotificationTypeFilterProps {
  hiddenTypes: readonly NotificationType[];
  onToggleGroup: (groupId: string) => void;
  /** Office / crew role so the list matches the crew-app preferences screen. */
  roleId?: string;
  /**
   * The trigger sits on the bell panel's forest header, so it takes the same
   * on-dark treatment as the activity feed's filter button: white-on-nav idle,
   * solid white when a filter is active.
   */
  onDark?: boolean;
}

export function NotificationTypeFilter({ hiddenTypes, onToggleGroup, onDark = false, roleId }: NotificationTypeFilterProps) {
  const filterActive = hiddenTypes.length > 0;
  const groups = getBellFilterGroupsForRole(roleId);

  return (
    <DropdownMenuSub>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuSubTrigger
              className={cn(
                'h-7 w-7 p-0 justify-center gap-0 rounded-md text-xs transition-colors',
                '[&>svg:last-child]:hidden',
                onDark
                  ? filterActive
                    ? 'bg-white text-stone-900 hover:bg-white/90 focus:bg-white/90 data-[state=open]:bg-white/90 data-[state=open]:text-stone-900'
                    : 'text-white/50 hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white data-[state=open]:bg-white/10 data-[state=open]:text-white'
                  : cn(
                      'focus:bg-stone-100 data-[state=open]:bg-stone-100',
                      filterActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                    ),
              )}
              aria-label={BELL_FILTER_BUTTON_TOOLTIP}
              title={BELL_FILTER_BUTTON_TOOLTIP}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Funnel className="h-3 w-3" weight={filterActive ? 'fill' : 'regular'} />
            </DropdownMenuSubTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">{BELL_FILTER_BUTTON_TOOLTIP}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenuPortal>
        <DropdownMenuSubContent className="w-60 p-1">
          <DropdownMenuLabel className="px-2 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400">
            Hide types
          </DropdownMenuLabel>
          {groups.map((group) => {
            const hidden = isBellFilterGroupHidden(hiddenTypes, group.id);
            return (
              <DropdownMenuItem
                key={group.id}
                title={group.tooltip}
                onSelect={(e) => {
                  e.preventDefault();
                  onToggleGroup(group.id);
                }}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer focus:bg-stone-100"
              >
                {hidden ? (
                  <CheckSquare className="h-3.5 w-3.5 text-primary shrink-0" />
                ) : (
                  <Square className="h-3.5 w-3.5 text-stone-400 shrink-0" />
                )}
                <span className="text-[13px] text-stone-700 flex-1">{group.label}</span>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}
